export enum ServiceStatus {
  ACTIVE = 1,
  UPCOMING = 2,
  EXPIRED = 3,
  ONE_TIME = 4,
  DRAFT = 5
}

export enum ServiceSourceType {
  DEAL = "Deal",
  CONTRACT = "Contract"
}

export interface ServiceData {
  id: string;
  serviceItemId: string | null;
  serviceItemName: string | null;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  autoRenewal: boolean;
  quantity: number | null;
  isDiscountPercentage: boolean;
  discountAmount: number | null;
  total: number | null;
  totalLocal: number | null;
  billingStartDate: string | null;
  billingEndDate: string | null;
  customBillingFrequency: string | null;
  customServiceDuration: string | null;
  // Backend-computed display labels (humanized enum/bool); preferred for
  // rendering and CSV export so labels stay consistent with the API contract.
  autoRenewalDisplay: string | null;
  customServiceDurationDisplay: string | null;
  customOptOutDate: string | null;
  customDuration: string | null;
  contractId: string | null;
  contractName: string | null;
  companyId: string | null;
  companyName: string | null;
  productId: string | null;
  productName: string | null;
  serviceType: number | null;
  chargeType: number | null;
  currency: string | null;
  csmOwner: string | null;
  csmOwnerName: string | null;
  aeOwner: string | null;
  aeOwnerName: string | null;
  pricingId: string | null;
  serviceItemPricingId: string | null;
  status: number | null;
  statusName: string | null;
  source: string | null;
  sourceType: string | null;
  dealId: string | null;
  dealsStage?: number | null;
  dealsStatus?: number | null;
  parentCompany?: string | null;
  durationMonths: number | null;
  durationDisplay: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: string | null;
  billingFrequencyDisplay: string | null;
  nextBillingDate: string | null;
  itemOrder: number;
  createdAt: string;
  note?: string | null;
  // The user id of whoever last wrote the note.
  noteUpdatedBy?: string | null;
}

// Typed field-key constants for ServicesTable — sort option keys, filter keys,
// and exclusion guards for HARDCODED_COLUMN_IDS. All visible columns are
// pipeline-driven except for the hardcoded ColumnDefs in ServicesTable.tsx.
export enum ServiceColumn {
  // Excluded from pipeline; no active ColumnDef (suppressed pending DB migration)
  BILLING_FREQUENCY = "eqPaymentsBillingFrequency",
  // Sort / filter keys
  COMPANY_NAME = "companyName",
  PRODUCT_NAME = "productName",
  SERVICE_ITEM_NAME = "serviceItemName",
  STATUS_NAME = "statusName",
  CURRENCY = "currency",
  CSM_OWNER = "csmOwner",
  AE_OWNER = "aeOwner",
  CUSTOM_SERVICE_DURATION = "customServiceDuration",
  CUSTOM_SERVICE_DURATION_DISPLAY = "customServiceDurationDisplay",
  DISCOUNT_AMOUNT = "discountAmount",
  AUTO_RENEWAL = "autoRenewal",
  AUTO_RENEWAL_DISPLAY = "autoRenewalDisplay",
  DEALS_STAGE = "dealsStage",
  DEALS_STATUS = "dealsStatus",
  PARENT_COMPANY = "parentCompany",
  COMPANY_ID = "companyId",
  PRODUCT_ID = "productId",
  STATUS = "status",
  SERVICE_ITEM_ID = "serviceItemId",
  SERVICE_ITEM_PRICING_ID = "serviceItemPricingId",
  CONTRACT_NAME = "contractName",
  SERVICE_TYPE = "serviceType",
  CHARGE_TYPE = "chargeType",
  NEXT_BILLING_DATE = "nextBillingDate",
  BILLING_END_DATE = "billingEndDate",
  SERVICE_END_DATE = "serviceEndDate",
  SERVICE_START_DATE = "serviceStartDate",
  TOTAL = "total",
  SOURCE = "source",
  SOURCE_TYPE = "sourceType",
  PRICING = "pricing",
  // The terms-currency half of each split money pair. `total`/`totalLocal` both
  // arrive from the pipeline; pricing is computed client-side from each.
  TOTAL_TERMS = "totalLocal",
  PRICING_TERMS = "pricingLocal",
  // Rendered as an icon that opens the note; not a registry field.
  NOTE = "note",
}

// Columns that may render in the services table UI but should not appear in
// the CSV export — raw FK IDs and the boolean discount-type flag have no
// useful value on a row-export and were previously emitted as blank cells.
// `pricing` and `pricingLocal` are intentionally NOT excluded: the backend
// emits computed pricing fields (total / quantity, totalLocal / quantity)
// matching the table's client-side values. `note` is NOT excluded either: the
// table shows it as an icon, but the export rows carry the note text.
export const SERVICES_EXPORT_EXCLUDED_COLUMNS: readonly string[] = [
  ServiceColumn.SERVICE_ITEM_PRICING_ID,
  "isDiscountPercentage"
];

// Columns whose id is not a field the API can sort on, mapped onto the
// revenue_items_view column behind the value the cell renders. A column id absent
// from the view is not an error the server reports — it drops the sort and returns
// rows in natural order, so the header shows a direction the rows do not follow.
//
// Company and Product are absent on purpose: the view carries company_name and
// product_name as real columns, so their own ids sort alphabetically — remapping
// them to the FK id sorted rows by an opaque "com-…" string instead.
export const SERVICES_SORT_FIELD_BY_COLUMN: Record<string, string> = {
  // Renders total and quantity together; total is the sortable half.
  [ServiceColumn.PRICING]: "total",
  // Both cells render a label derived from the raw column named here.
  [ServiceColumn.AUTO_RENEWAL_DISPLAY]: ServiceColumn.AUTO_RENEWAL,
  [ServiceColumn.CUSTOM_SERVICE_DURATION_DISPLAY]:
    ServiceColumn.CUSTOM_SERVICE_DURATION
};

export interface ServicesPage {
  revenue_items?: ServiceData[];
  revenueItemsEnriched?: ServiceData[];
  total: number;
  page: number;
  size: number;
  pages: number;
  aggregations: Record<string, unknown>;
}
